import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, Home, ArrowLeft, Search, MessageSquare, Phone } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import SEO from "@/components/SEO";

export default function NotFound() {
  const { settings } = useSettings();
  const siteName = settings.site_name || "HyderabadTourism.in";
  return (
    <div className="min-h-[80vh] flex flex-col items-center justify-center text-center px-4 py-16" data-testid="not-found-page">
      <SEO title="Page Not Found (404)" description={`This page does not exist on ${siteName}. Explore our tours, hotels, and travel packages.`} />

      {/* Animated map pin */}
      <div className="relative mb-8">
        <div className="w-28 h-28 rounded-3xl shadow-xl" style={{ background: "linear-gradient(135deg, #00B7EB 0%, #006B85 100%)" }}>
          <div className="absolute inset-0 flex items-center justify-center">
            <MapPin className="w-14 h-14 text-white animate-bounce" strokeWidth={1.8} style={{ animationDuration: "2s" }} />
          </div>
        </div>
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-16 h-3 bg-black/10 rounded-full blur-sm" />
      </div>

      <div className="font-display text-7xl sm:text-8xl mb-3" style={{ color: "#00B7EB" }}>404</div>
      <h1 className="font-display text-2xl sm:text-3xl mb-3">Looks like you're off the map!</h1>
      <p className="text-muted-foreground max-w-md mb-8 text-sm sm:text-base">
        The page you're looking for doesn't exist or has been moved.
        Let's get you back to exploring Hyderabad.
      </p>

      <div className="flex flex-wrap gap-3 justify-center mb-10">
        <Link to="/">
          <Button className="bg-saffron hover:bg-saffron-dark text-white" data-testid="404-home">
            <Home className="w-4 h-4 mr-1.5" />Home
          </Button>
        </Link>
        <Link to="/search">
          <Button variant="outline" data-testid="404-explore">
            <Search className="w-4 h-4 mr-1.5" />Explore Packages
          </Button>
        </Link>
        <Link to="/enquire">
          <Button variant="outline" data-testid="404-enquire">
            <MessageSquare className="w-4 h-4 mr-1.5" />Enquire Now
          </Button>
        </Link>
        <Button variant="ghost" onClick={() => window.history.back()} data-testid="404-back">
          <ArrowLeft className="w-4 h-4 mr-1.5" />Go Back
        </Button>
      </div>

      {/* Quick help */}
      <div className="bg-muted/50 rounded-2xl p-6 max-w-md w-full">
        <p className="text-sm text-muted-foreground mb-3">Need help finding something?</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href={`tel:${(settings.contact_phone || "+919000000000").replace(/\s/g, "")}`} className="flex items-center justify-center gap-2 text-sm text-teal font-medium hover:underline">
            <Phone className="w-4 h-4" /> Call us
          </a>
          <Link to="/enquire" className="flex items-center justify-center gap-2 text-sm text-teal font-medium hover:underline">
            <MessageSquare className="w-4 h-4" /> Send an enquiry
          </Link>
        </div>
      </div>
    </div>
  );
}
